import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import api from "@/lib/api";
import { useFilters } from "@/context/FiltersContext";
import { money, haversineKm, SIDEBAR_ASSOCIATIONS, canonEvent, moneyLabel, dollarsPerMile, mountainTodayISO } from "@/lib/ui";
import { EntryChips } from "@/components/EntryChips";
import useHome from "@/lib/useHome";
import { getChoices, rodeoEntered, rodeoTouched } from "@/lib/choices";
import usePersistentState from "@/lib/usePersistentState";
import { useSheetSize } from "@/lib/useSheetSize";
import { M } from "@/lib/mobileTheme";
import { Loader2, Plus, Check, Route, X, Navigation, Wand2, Clock, Fuel, LocateFixed, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Search, Users } from "lucide-react";
import MobileViewHeader from "@/components/mobile/MobileViewHeader";
import { toast } from "sonner";
import ContestantCard from "@/components/ContestantCard";
import useFollowing from "@/lib/useFollowing";
import { useAuth } from "@/context/AuthContext";
import { Star, Crown } from "lucide-react";
import { MRodeoActions, MRodeoDialogs } from "@/components/mobile/rodeoDialogs";

const TILES = {
  street: { url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: "&copy; OpenStreetMap" },
  satellite: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: "Tiles &copy; Esri, Maxar, Earthstar Geographics" },
};
const COLORS = { green: "#3B8A2B", amber: "#F59E0B", red: "#C8000F", grey: "#9AA1AB", sel: "#3B82F6", home: "#E68A2E", me: "#0EA5E9", cpra: "#C8000F", prca: "#3B82F6", cca: "#A855F7" };
const assocKey = (r) => (r.associations || []).includes("CPRA") ? "cpra" : (r.associations || []).includes("PRCA") ? "prca" : "cca";
function statusColor(id, choices, drawKnown) {
  const entered = !!(choices[id]?.pref1 || choices[id]?.pref2);
  if (!entered) return "grey";
  return drawKnown ? "green" : "amber";
}
function pinIcon(color, n) {
  const inner = n ? n : "";
  const size = n ? "width:32px;height:32px;font-size:14px;" : "width:20px;height:20px;";
  return L.divIcon({ className: "", iconSize: [32, 32], iconAnchor: [16, 16], html: `<div class="rp-map-pin" style="background:${COLORS[color]};${size}">${inner}</div>` });
}
function homeIcon() {
  return L.divIcon({ className: "", iconSize: [30, 30], iconAnchor: [15, 15], html: `<div class="rp-map-pin" style="background:${COLORS.home};border:2px solid #fff;font-size:15px">⌂</div>` });
}
function meIcon() {
  return L.divIcon({ className: "", iconSize: [22, 22], iconAnchor: [11, 11], html: `<div style="width:16px;height:16px;border-radius:50%;background:${COLORS.me};border:3px solid #fff;box-shadow:0 0 0 3px ${COLORS.me}55"></div>` });
}
function hiIcon(n) {
  return L.divIcon({ className: "", iconSize: [34, 34], iconAnchor: [17, 17], html: `<div class="rp-map-pin" style="background:#3B82F6;width:22px;height:22px;font-size:11px;border:2px solid #fff;box-shadow:0 0 0 5px #3B82F655">${n || ""}</div>` });
}
function FlyTo({ loc, zoom = 9 }) {
  const map = useMap();
  useEffect(() => { if (loc && loc.lat && loc.lng) map.flyTo([loc.lat, loc.lng], zoom, { duration: 0.8 }); }, [loc, zoom, map]);
  return null;
}

export default function MobileMap() {
  const nav = useNavigate();
  const { matches, weekStart: weekStartISO, shiftWeeks, weekToday, rodeos, prcaRodeos, ccaRodeos, events, displayEvents, sortBy, dateRange, selectRodeo, selectedRodeoId, clearSelection } = useFilters();
  const forcedSel = useMemo(() => {
    if (!selectedRodeoId) return null;
    const r = (rodeos || []).find((x) => x.id === selectedRodeoId);
    return r && !matches(r) ? r : null;
  }, [selectedRodeoId, rodeos, matches]);
  const home = useHome();
  const [sel, setSel] = usePersistentState("rp_map_route", []);
  const [allDates, setAllDates] = usePersistentState("rp_map_alldates_v2", true);
  const [mapType, setMapType] = usePersistentState("rp_map_type", "street");
  const [roundTrip, setRoundTrip] = usePersistentState("rp_map_roundtrip", false);
  const [cMode, setCMode] = usePersistentState("rp_map_riders_mode", false);
  const { user } = useAuth();
  const isPro = !!user?.is_pro;
  const { following, isFollowing, follow, unfollow } = useFollowing();
  const [searchParams] = useSearchParams();
  const [profiles, setProfiles] = useState({});   // rider name -> profile
  const [activeRider, setActiveRider] = useState(null); // null = all followed riders
  const [rQ, setRQ] = useState("");
  const [rResults, setRResults] = useState(null);
  const [riderPopup, setRiderPopup] = useState(null);
  const [cEvent, setCEvent] = usePersistentState("rp_map_cevent", "Team Roping");
  const [cData, setCData] = useState(null);
  const [card, setCard] = useState(null);
  const [cQ, setCQ] = useState("");
  const [enteredOnly, setEnteredOnly] = usePersistentState("rp_map_entered_only", false);
  const pinColorFor = (r) => (enteredOnly ? "green" : assocKey(r));
  const [mapQ, setMapQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);
  const [focusId, setFocusId] = useState(selectedRodeoId);
  useEffect(() => { if (selectedRodeoId) setFocusId(selectedRodeoId); }, [selectedRodeoId]);
  const sheet = useSheetSize("rp_m_map_sheet_v2");
  const [dlg, setDlg] = useState(null);
  const sortLabel = sortBy === "near" ? "nearest first" : sortBy === "money" ? "biggest purse" : sortBy === "value" ? "best $/mi" : "by date";
  const choices = getChoices();

  const allRodeos = useMemo(() => (rodeos || []), [rodeos]);

  const allEvents = useMemo(() => [...new Set((rodeos || []).flatMap((r) => (r.events || []).map(canonEvent)).filter(Boolean))].sort(), [rodeos]);
  // Enter Riders mode (and focus a rider) when arriving from My Riders via ?rider=Name
  useEffect(() => {
    const rp = searchParams.get("rider");
    if (rp) { setCMode(true); setActiveRider(rp); if (!isFollowing(rp)) follow(rp); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Remember the rider focused on the Map so tapping "My Riders" opens that rider's card.
  useEffect(() => {
    try {
      if (cMode && activeRider) localStorage.setItem("rp-focus-rider", activeRider);
      else localStorage.removeItem("rp-focus-rider");
    } catch {}
  }, [cMode, activeRider]);
  // Fetch each followed rider's profile (pins/results) once we're in Riders mode.
  useEffect(() => {
    if (!cMode) return;
    const names = activeRider ? [activeRider] : following;
    names.forEach((n) => {
      if (!profiles[n]) {
        api.get(`/competitors/profile?name=${encodeURIComponent(n)}`)
          .then((r) => setProfiles((p) => ({ ...p, [n]: r.data }))).catch(() => {});
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cMode, following, activeRider]);
  // Rider name search (reuses the contestant search).
  useEffect(() => {
    const qq = rQ.trim();
    if (qq.length < 2) { setRResults(null); return; }
    const t = setTimeout(() => api.get(`/competitors/search?q=${encodeURIComponent(qq)}`).then((r) => setRResults(r.data || [])).catch(() => setRResults([])), 300);
    return () => clearTimeout(t);
  }, [rQ]);
  const riderColor = (status) => status === "won" ? "green" : status === "upcoming" ? "sel" : "grey";
  const riderPins = useMemo(() => {
    const names = activeRider ? [activeRider] : following;
    const out = [];
    names.forEach((n) => {
      const prof = profiles[n];
      if (!prof) return;
      (prof.pins || []).forEach((pin) => { if (pin.lat && pin.lng) out.push({ rider: n, ...pin }); });
    });
    return out;
  }, [profiles, following, activeRider]);
  const cById = useMemo(() => Object.fromEntries((cData?.rodeos || []).map((r) => [r.rodeo_id, r])), [cData]);

  const wStart = useMemo(() => new Date(weekStartISO + "T00:00:00"), [weekStartISO]);
  const inSharedWindow = (rodeo) => {
    const dates = (rodeo.performances || []).map((p) => p.date).filter(Boolean);
    if (!dates.length) return false;
    const end = new Date(wStart); end.setDate(wStart.getDate() + 14);
    return dates.some((d) => { const dt = new Date(d + "T00:00:00"); return dt >= wStart && dt < end; });
  };
  const rangeLabel = `${wStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(wStart.getTime() + 13 * 864e5).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;

  const list = useMemo(() => {
    const seen = new Set();
    // Mirror the Table EXACTLY: same source, same rules, same "hide already-finished
    // rodeos" floor (forward-looking ranges are floored at today; a fully-past range shows
    // past rodeos). Entered/selected rodeos are still force-included below.
    const todayISO = mountainTodayISO();
    const rangeIsPast = !!(dateRange?.to && dateRange.to < todayISO);
    const hasFuture = (r) => (r.performances || []).some((p) => p.date && p.date >= todayISO);
    const src = allRodeos.filter((r) => {
      if (seen.has(r.id)) return false; seen.add(r.id);
      if (enteredOnly) return rodeoEntered(choices, r.id);
      return (rangeIsPast || hasFuture(r)) && matches(r);
    });
    let arr = src
      .map((r) => ({ ...r, km: home && r.lat && r.lng ? Math.round(haversineKm(home, { lat: r.lat, lng: r.lng })) : Infinity }))
      // Mirror the Table's ordering so every view reads in the same sequence.
      .sort((a, b) => {
        if (sortBy === "money") return (b.added_money || 0) - (a.added_money || 0);
        if (sortBy === "near") return a.km - b.km;
        if (sortBy === "value") return (dollarsPerMile(b, home, events) ?? -1) - (dollarsPerMile(a, home, events) ?? -1);
        return ((a.performances?.[0]?.date) || "9999").localeCompare((b.performances?.[0]?.date) || "9999");
      });
    // Always render the selected rodeo (e.g. picked from Table/Calendar) so its pin highlights here too.
    if (selectedRodeoId && !arr.some((r) => r.id === selectedRodeoId)) {
      const selR = allRodeos.find((r) => r.id === selectedRodeoId);
      if (selR) arr = [{ ...selR, km: home && selR.lat && selR.lng ? Math.round(haversineKm(home, { lat: selR.lat, lng: selR.lng })) : Infinity }, ...arr];
    }
    // The selected rodeo is pinned to the TOP of the list (matches its highlight).
    if (selectedRodeoId) {
      const i = arr.findIndex((r) => r.id === selectedRodeoId);
      if (i > 0) { const [s] = arr.splice(i, 1); arr.unshift(s); }
    }
    return arr;
  }, [allRodeos, matches, home, enteredOnly, choices, selectedRodeoId, sortBy, events, dateRange]);

  // When a rodeo is selected from another view, pan the map to it so the highlight is visible.
  useEffect(() => {
    if (!selectedRodeoId) return;
    const r = allRodeos.find((x) => x.id === selectedRodeoId);
    if (r && r.lat && r.lng) setFlyTarget({ lat: r.lat, lng: r.lng, _k: Date.now() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRodeoId]);

  // Contestant pins follow the same filtered list (association + date), overlaying draw data where present.
  const cRodeos = useMemo(() => {
    const qq = cQ.trim().toLowerCase();
    return list.map((r) => {
      const cd = cById[r.id];
      const contestants = cd?.contestants || [];
      const matched = qq ? contestants.filter((n) => n.toLowerCase().includes(qq)) : [];
      return { rodeo_id: r.id, name: r.name, lat: r.lat, lng: r.lng, city: r.city,
               associations: r.associations, count: contestants.length, contestants, matched, hasData: !!cd };
    }).filter((r) => !qq || r.matched.length);
  }, [list, cById, cQ]);
  const cWithData = useMemo(() => cRodeos.filter((r) => r.hasData).length, [cRodeos]);

  const byId = Object.fromEntries(allRodeos.map((r) => [r.id, r]));
  const selRodeos = sel.map((id) => byId[id]).filter(Boolean);
  const toggleSel = (id) => setSel((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const firstDate = (r) => { const ds = (r.performances || []).map((p) => p.date).filter(Boolean).sort(); return ds[0] || r.start_date || ""; };
  const drawKnown = (r) => !!(r.real_draw || r.has_draw || (r.draw && (Array.isArray(r.draw) ? r.draw.length : true)));
  const dateOrder = useMemo(() => {
    const ordered = [...list].sort((a, b) => (firstDate(a) || "9999").localeCompare(firstDate(b) || "9999"));
    const mp = {}; ordered.forEach((r, i) => { mp[r.id] = i + 1; }); return mp;
  }, [list]);
  const mapMatches = useMemo(() => {
    const q = mapQ.trim().toLowerCase();
    if (!q) return [];
    return allRodeos.filter((r) => r.lat && r.lng && `${r.name} ${r.city} ${r.state}`.toLowerCase().includes(q)).slice(0, 10);
  }, [mapQ, allRodeos]);
  const goToRodeo = (r) => {
    setFocusId(r.id);
    selectRodeo(r);
    setMapQ("");
    setSearchOpen(false);
    if (r.lat && r.lng) setFlyTarget({ lat: r.lat, lng: r.lng, _k: Date.now() });
    if (window.matchMedia("(orientation: portrait)").matches) sheet.setCollapsed(true);
  };
  const [myLoc, setMyLoc] = useState(null);
  const locate = () => {
    if (!navigator.geolocation) return toast.error("Location isn't available on this device");
    toast.loading("Finding your location…", { id: "loc" });
    navigator.geolocation.getCurrentPosition(
      (pos) => { setMyLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }); toast.success("Centered on your location", { id: "loc" }); },
      () => toast.error("Couldn't get your location — check permissions", { id: "loc" }),
      { enableHighAccuracy: true, timeout: 8000 });
  };

  const HOME = { name: home.town ? `Home (${home.town})` : "Home base", lat: home.lat, lng: home.lng };
  const routeStops = selRodeos.length ? [HOME, ...selRodeos, ...(roundTrip ? [{ ...HOME, name: "Home (return)" }] : [])] : [];
  const outbound = selRodeos.length ? [HOME, ...selRodeos] : [];
  const returnLeg = roundTrip && selRodeos.length ? [selRodeos[selRodeos.length - 1], HOME] : [];
  const legs = []; let total = 0;
  for (let i = 1; i < routeStops.length; i++) { const km = Math.round(haversineKm(routeStops[i - 1], routeStops[i])); total += km; legs.push({ from: routeStops[i - 1], to: routeStops[i], km }); }

  const wp = routeStops.map((r) => `${r.lat},${r.lng}`);
  const googleUrl = wp.length ? `https://www.google.com/maps/dir/?api=1&origin=${wp[0]}&destination=${wp[wp.length - 1]}&travelmode=driving${wp.length > 2 ? `&waypoints=${wp.slice(1, -1).join("|")}` : ""}` : "";
  const appleUrl = `http://maps.apple.com/?saddr=${wp[0] || ""}&daddr=${wp.slice(1).join("+to:")}`;

  const AVG_KMH = 95, L_PER_100 = 13, FUEL_PRICE = 1.65;
  const driveH = total / AVG_KMH;
  const [road, setRoad] = useState(null);
  const wpKey = routeStops.map((r) => `${r.lat},${r.lng}`).join("|");
  useEffect(() => {
    if (routeStops.length < 2) { setRoad(null); return; }
    let abort = false;
    const coords = routeStops.map((r) => `${r.lng},${r.lat}`).join(";");
    fetch(`https://router.project-osrm.org/route/v1/driving/${coords}?overview=false`)
      .then((r) => r.json())
      .then((d) => { if (abort) return; const rt = d?.routes?.[0]; setRoad(rt ? { km: Math.round(rt.distance / 1000), h: rt.duration / 3600 } : null); })
      .catch(() => { if (!abort) setRoad(null); });
    return () => { abort = true; };
  }, [wpKey]);
  const showKm = road ? road.km : total;
  const showH = road ? road.h : driveH;
  const fuelCost = (showKm / 100) * L_PER_100 * FUEL_PRICE;

  const optimize = () => {
    if (selRodeos.length < 2) return;
    const remaining = [...selRodeos]; const order = []; let cur = HOME;
    while (remaining.length) { let bi = 0, bd = Infinity; remaining.forEach((r, i) => { const d = haversineKm(cur, r); if (d < bd) { bd = d; bi = i; } }); const nx = remaining.splice(bi, 1)[0]; order.push(nx.id); cur = nx; }
    setSel(order); toast.success("Route optimized from home");
  };

  return (
    <div className="flex flex-col bg-[#0C1017] text-slate-100 h-[calc(100dvh_-_var(--hdr)_-_var(--ftr))] transition-[height] duration-200 overflow-hidden" data-testid="mobile-map">
      {searchOpen && (
        <div className="fixed inset-0 z-[3000] bg-[#0C1017] flex flex-col" data-testid="m-map-search-overlay">
          <div className="shrink-0 flex items-center gap-2 p-3 border-b border-white/10 bg-[#0F141B]" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
            <div className="relative flex-1 min-w-0">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input autoFocus value={mapQ} onChange={(e) => setMapQ(e.target.value)} placeholder="Search a rodeo…" data-testid="m-map-search-input"
                className="w-full text-[16px] bg-white/5 border border-white/10 text-slate-100 placeholder-slate-500 rounded-lg pl-9 pr-3 py-2.5 focus:outline-none focus:border-[#C8000F]" />
            </div>
            <button onClick={() => { setSearchOpen(false); setMapQ(""); }} data-testid="m-map-search-cancel"
              className="shrink-0 px-3 py-2 text-[14px] font-semibold text-slate-300 active:text-white">Cancel</button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            {mapQ.trim() ? (
              mapMatches.length ? mapMatches.map((r) => (
                <button key={r.id} data-testid={`m-map-search-result-${r.id}`} onClick={() => goToRodeo(r)}
                  className="flex items-center gap-3 w-full text-left px-4 py-3 border-b border-white/5 active:bg-white/5">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: COLORS[pinColorFor(r)] }} />
                  <span className="min-w-0">
                    <span className="block text-[15px] font-semibold text-white truncate">{r.name}</span>
                    <span className="block text-[12px] text-slate-400 truncate">{[r.city, r.state].filter(Boolean).join(", ")}</span>
                  </span>
                </button>
              )) : <div className="px-4 py-6 text-[13px] text-slate-500">No rodeos match “{mapQ}”.</div>
            ) : (
              <div className="px-4 py-6 text-[13px] text-slate-500">Start typing a rodeo name, city or province…</div>
            )}
          </div>
        </div>
      )}
      <MobileViewHeader onSearch={() => setSearchOpen(true)} searchLabel="Search a rodeo…" searchTestid="m-map-search-open">
        <div className="flex items-center gap-1.5" data-testid="m-map-tools">
          <button onClick={() => { setEnteredOnly((v) => !v); setFocusId(null); }} data-testid="m-map-entered-toggle" title="Show only rodeos you've entered (green)"
            className={`shrink-0 justify-center px-2.5 py-2 rounded-lg border flex items-center gap-1 text-[12px] font-semibold ${enteredOnly ? "bg-[#16A34A] border-[#16A34A] text-white" : "bg-white/5 border-white/10 text-slate-300"}`}>
            <Check className="w-4 h-4" /> Entered
          </button>
          <div className="flex flex-1 rounded-lg overflow-hidden border border-white/10 items-stretch" data-testid="m-map-view-mode">
            <button onClick={() => setCMode(false)} data-testid="m-map-mode-rodeos" className={`flex-1 justify-center text-[13px] font-bold px-3 py-2 ${!cMode ? "bg-[#C8000F] text-white" : "bg-white/5 text-slate-400"}`}>Rodeos</button>
            <button onClick={() => setCMode(true)} data-testid="m-map-mode-contestants" className={`flex-1 justify-center text-[13px] font-bold px-3 py-2 flex items-center gap-1.5 ${cMode ? "bg-[#C8000F] text-white" : "bg-white/10 text-white"}`}><Users className="w-3.5 h-3.5" /> Riders</button>
          </div>
        </div>
        {cMode && (
          <div className="space-y-1.5" data-testid="m-map-rider-tools">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
              <input value={rQ} onChange={(e) => setRQ(e.target.value)} placeholder="Search a rider to follow…" data-testid="m-map-rider-search"
                className="w-full text-[16px] bg-white/5 border border-white/10 text-slate-100 placeholder-slate-500 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:border-[#C8000F]" />
            </div>
            {rQ.trim().length >= 2 && (
              <div className="rounded-lg bg-[#161B22] border border-white/10 max-h-44 overflow-y-auto" data-testid="m-map-rider-results">
                {rResults && rResults.length ? rResults.map((r) => {
                  const f = isFollowing(r.name);
                  return (
                    <button key={r.name} onClick={() => { f ? unfollow(r.name) : follow(r.name); }} data-testid={`m-map-rider-follow-${r.name}`}
                      className="flex items-center justify-between gap-2 w-full text-left px-3 py-2 border-b border-white/5 last:border-0 active:bg-white/5">
                      <span className="min-w-0"><span className="block text-[14px] font-semibold text-white truncate">{r.name}</span>{r.hometown && <span className="block text-[11px] text-slate-500 truncate">{r.hometown}</span>}</span>
                      <Star className={`w-4 h-4 shrink-0 ${f ? "fill-current text-[#C8000F]" : "text-slate-400"}`} />
                    </button>
                  );
                }) : <div className="px-3 py-4 text-[12px] text-slate-500">No riders match “{rQ}”.</div>}
              </div>
            )}
            {following.length > 0 && (
              <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-0.5" data-testid="m-map-rider-chips">
                <button onClick={() => setActiveRider(null)} className={`shrink-0 text-[12px] font-semibold px-2.5 py-1 rounded-full ${!activeRider ? "bg-[#C8000F] text-white" : "bg-white/5 text-slate-300"}`}>All ({following.length})</button>
                {following.map((n) => (
                  <button key={n} onClick={() => setActiveRider(activeRider === n ? null : n)} data-testid={`m-map-rider-chip-${n}`}
                    className={`shrink-0 text-[12px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${activeRider === n ? "bg-[#C8000F] text-white" : "bg-white/5 text-slate-300"}`}>{n}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </MobileViewHeader>
      {forcedSel && (
        <div className="shrink-0 px-2.5 py-2 bg-[#0F141B] border-b border-white/10">
          <button onClick={clearSelection} data-testid="m-map-clear-search" className="w-full flex items-center justify-center gap-2 text-[12px] font-semibold bg-[#C8000F]/15 text-[#F26D77] px-3 py-2 rounded-lg">
            Showing “{forcedSel.name}” · Back to my list <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div className="relative flex-1 min-h-0">
      <div className="relative h-full w-full">
        <div className="hidden max-lg:landscape:flex absolute top-2.5 left-2.5 z-[1102] items-start gap-2" data-testid="m-map-ls-controls">
          <button onClick={() => { setEnteredOnly((v) => !v); setFocusId(null); }} data-testid="m-map-entered-toggle-ls"
            className={`shrink-0 px-2.5 py-1.5 rounded-lg border flex items-center gap-1.5 text-[12px] font-semibold shadow-md ${enteredOnly ? "bg-[#16A34A] border-[#16A34A] text-white" : "bg-[#161B22]/90 border-white/15 text-slate-200"}`}>
            <Check className="w-4 h-4" /> Entered
          </button>
          <button type="button" onClick={() => setSearchOpen(true)} data-testid="m-map-search-open-ls"
            className="relative w-44 flex items-center gap-2 text-left bg-[#161B22]/95 border border-white/15 text-slate-300 rounded-lg pl-8 pr-3 py-1.5 text-[13px] shadow-md active:bg-[#161B22]">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <span className="truncate">Search rodeo…</span>
          </button>
        </div>
        <div className="absolute top-2.5 right-2.5 z-[1000] flex items-center gap-2" data-testid="m-map-type-toggle">
          <button onClick={locate} data-testid="m-map-locate-btn" title="Track me"
            className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg shadow-md border border-white/10 bg-[#161B22] text-slate-200">
            <LocateFixed className="w-3.5 h-3.5 text-[#0EA5E9]" /> Track me
          </button>
          <div className="flex rounded-lg overflow-hidden shadow-md border border-white/10">
            <button onClick={() => setMapType("street")} data-testid="m-map-type-street"
              className={`text-[11px] font-semibold px-2.5 py-1.5 ${mapType === "street" ? "bg-[#3B82F6] text-white" : "bg-[#161B22] text-slate-300"}`}>Map</button>
            <button onClick={() => setMapType("satellite")} data-testid="m-map-type-satellite"
              className={`text-[11px] font-semibold px-2.5 py-1.5 ${mapType === "satellite" ? "bg-[#3B82F6] text-white" : "bg-[#161B22] text-slate-300"}`}>Satellite</button>
          </div>
        </div>
        {rodeos === null ? (
          <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
        ) : (
          <MapContainer center={[52, -108]} zoom={4} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer url={TILES[mapType].url} attribution={TILES[mapType].attribution} />
            {mapType === "satellite" && <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}" />}
            <FlyTo loc={myLoc} />
            <FlyTo loc={flyTarget} zoom={10} />
            {myLoc && <Marker position={[myLoc.lat, myLoc.lng]} icon={meIcon()}><Popup><div className="font-semibold">You are here</div></Popup></Marker>}
            <Marker position={[HOME.lat, HOME.lng]} icon={homeIcon()}><Popup><div className="font-semibold">Your home base</div></Popup></Marker>
            {outbound.length > 1 && <Polyline positions={outbound.map((r) => [r.lat, r.lng])} pathOptions={{ color: "#3B82F6", weight: 3, dashArray: "6 8" }} />}
            {returnLeg.length === 2 && <Polyline positions={returnLeg.map((r) => [r.lat, r.lng])} pathOptions={{ color: "#E68A2E", weight: 3 }} />}
            {cMode ? riderPins.map((p, idx) => (
              <Marker key={`${p.rider}-${p.rodeo_id}-${idx}`} position={[p.lat, p.lng]} icon={pinIcon(riderColor(p.status))}
                eventHandlers={{ click: () => setRiderPopup(p) }}>
                <Popup>
                  <div className="font-semibold">{p.rider}</div>
                  <div className="text-slate-500">{p.name}{p.city ? ` · ${p.city}` : ""}</div>
                  {p.status === "upcoming" ? (
                    <div className="mt-1 text-slate-600">
                      <div className="text-[#1D4ED8] font-semibold">Entered — up next</div>
                      {p.upcoming.map((u, i) => (
                        <div key={i} className="mt-0.5">{u.event}{u.role ? ` (${u.role})` : ""} · {u.performance_label || ""}{u.performance_date ? ` · ${u.performance_date}` : ""}{u.position ? ` · #${u.position}` : ""}</div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-1 text-slate-600">
                      <div className={`font-semibold ${p.status === "won" ? "text-[#16A34A]" : "text-slate-500"}`}>{p.status === "won" ? `Won ${money(p.earnings)}` : "Competed · no cheque"}</div>
                      <div className="mt-0.5 max-h-40 overflow-y-auto">
                        {p.results.map((r, i) => (
                          <div key={i}>{r.event} · {r.place ? `${r.place}` : "—"}{r.value ? ` · ${r.value}` : ""}{r.earnings > 0 ? ` · ${money(r.earnings)}` : ""}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </Popup>
              </Marker>
            )) : (<>
            {list.filter((r) => r.lat && r.lng).map((r, i) => (
              <Marker key={r.id} position={[r.lat, r.lng]} icon={enteredOnly ? pinIcon("green") : (focusId === r.id ? hiIcon(sel.includes(r.id) ? sel.indexOf(r.id) + 1 : dateOrder[r.id]) : pinIcon(sel.includes(r.id) ? "sel" : pinColorFor(r), sel.includes(r.id) ? sel.indexOf(r.id) + 1 : dateOrder[r.id]))}
                eventHandlers={{ click: () => { setFocusId(r.id); selectRodeo(r); } }}>
                <Popup>
                  <div className="font-semibold">{r.name}</div>
                  <div className="text-slate-500">{r.city}, {r.state}</div>
                  <div className="text-slate-600 mt-0.5">{moneyLabel(r, displayEvents)}{isFinite(r.km) ? ` · ${r.km.toLocaleString()} km away` : ""}</div>
                  <div className="text-slate-600 mt-0.5">{sel.includes(r.id) ? `On your trip · Stop #${sel.indexOf(r.id) + 1}` : "Selected — showing across all views"}</div>
                  <div className="flex items-center gap-3 mt-1.5">
                    <button onClick={() => setDlg({ type: "info", rodeo: r })} data-testid={`m-map-popup-info-${r.id}`} className="text-[#C8000F] font-semibold">Info</button>
                    <button onClick={() => toggleSel(r.id)} data-testid={`m-map-popup-trip-${r.id}`} className="text-[#1D4ED8] font-semibold">{sel.includes(r.id) ? "Remove from trip" : "Add to trip"}</button>
                  </div>
                </Popup>
              </Marker>
            ))}
            </>)}
          </MapContainer>
        )}
      </div>

      <div style={{ height: sheet.height }} className={`absolute z-[1100] bg-[#0C1017] ${sheet.collapsed ? "overflow-hidden" : "overflow-y-auto"} transition-transform duration-300 left-0 right-0 bottom-0 rounded-t-2xl border-t border-white/10 shadow-[0_-8px_30px_rgba(0,0,0,0.55)] landscape:!h-full landscape:top-0 landscape:bottom-0 landscape:right-auto landscape:w-[340px] landscape:rounded-none landscape:rounded-r-2xl landscape:border-t-0 landscape:border-r landscape:border-white/10 landscape:shadow-[8px_0_30px_rgba(0,0,0,0.6)] ${sheet.collapsed ? "landscape:-translate-x-full" : "landscape:translate-x-0"}`} data-testid="m-map-sheet">
      <div {...sheet.grabberProps} data-testid="m-map-sheet-grabber"
        className="landscape:hidden sticky top-0 z-10 w-full bg-[#0C1017]/95 backdrop-blur pt-2 pb-2 border-b border-white/10 flex flex-col items-center select-none">
        <span className="w-10 h-1.5 rounded-full bg-white/25 mb-1.5" />
        <span className="flex items-center gap-2 text-[13px] font-bold text-slate-200">
          {sheet.collapsed ? "Drag up or tap to show list" : (cMode ? `My Riders (${following.length})` : `${list.length} rodeos · ${sortLabel}`)}
          {sheet.collapsed ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </div>
      {!sheet.collapsed && (<>
      {selRodeos.length > 0 && (
        <div className="m-3 rounded-xl bg-[#12233B] border border-[#1E3A5F] p-3.5" data-testid="m-distance-panel">
          <div className="flex items-center justify-between mb-2">
            <span className="flex items-center gap-2 text-sm font-bold text-[#7FB3F0]"><Route className="w-4 h-4" /> Trip from Home</span>
            <div className="flex items-center gap-3">
              {selRodeos.length > 1 && <button onClick={optimize} data-testid="m-optimize-btn" className="flex items-center gap-1 text-xs font-semibold text-[#7FB3F0]"><Wand2 className="w-3.5 h-3.5" /> Optimize</button>}
              <button onClick={() => setSel([])} className="text-xs text-slate-400 flex items-center gap-1"><X className="w-3 h-3" /> Clear</button>
            </div>
          </div>
          <label className="flex items-center gap-2 mb-2 cursor-pointer select-none" data-testid="m-round-trip-toggle">
            <span className={`relative w-9 h-5 rounded-full transition-colors ${roundTrip ? "bg-[#3B82F6]" : "bg-slate-600"}`}>
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${roundTrip ? "translate-x-4" : ""}`} />
            </span>
            <input type="checkbox" checked={roundTrip} onChange={(e) => setRoundTrip(e.target.checked)} className="sr-only" />
            <span className="text-xs font-semibold text-slate-300">Return home after last rodeo</span>
          </label>
          {legs.map((l, i) => (
            <div key={i} className="flex items-center justify-between text-xs text-slate-300 py-0.5">
              <span className="truncate mr-2">{l.from.name} → {l.to.name}</span>
              <span className="font-mono font-semibold whitespace-nowrap">{l.km.toLocaleString()} km</span>
            </div>
          ))}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#1E3A5F] text-sm">
            <span className="font-semibold text-slate-200">{selRodeos.length} stop(s) · {road ? "by road" : "straight line"}</span>
            <span className="font-mono font-bold text-[#7FB3F0]" data-testid="m-distance-total">{showKm.toLocaleString()} km</span>
          </div>
          <div className="flex items-center justify-between text-xs text-slate-400 mt-1">
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> ~{showH.toFixed(1)} h drive</span>
            <span className="flex items-center gap-1"><Fuel className="w-3.5 h-3.5" /> ~${fuelCost.toFixed(0)} fuel</span>
          </div>
          <div className="text-[11px] text-slate-400 mt-1">Tap pins on the map to add stops — the route starts at your home base.</div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => window.open(googleUrl, "_blank")} data-testid="m-open-google-maps" className="flex-1 flex items-center justify-center gap-1.5 bg-[#3B82F6] text-white text-xs font-semibold py-2.5 rounded-lg"><Navigation className="w-3.5 h-3.5" /> Google Maps</button>
            <button onClick={() => window.open(appleUrl, "_blank")} data-testid="m-open-apple-maps" className="flex-1 flex items-center justify-center gap-1.5 bg-white/10 text-white text-xs font-semibold py-2.5 rounded-lg"><Navigation className="w-3.5 h-3.5" /> Apple Maps</button>
          </div>
        </div>
      )}

      <div className="hidden landscape:block px-4 py-2 text-sm font-bold text-slate-300">{cMode ? "My Riders" : `${list.length} Rodeos · ${sortLabel}`}</div>
      {cMode ? (
        <div className="px-3 pb-4 space-y-2.5" data-testid="m-map-riders-sheet">
          <div className="flex items-center gap-3 text-[11px] text-slate-400 px-1" data-testid="m-map-rider-legend">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS.green }} /> Won $</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS.grey }} /> No cheque</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS.sel }} /> Entered next</span>
          </div>
          {!following.length ? (
            <div className="text-center py-10 px-6"><p className="text-slate-500 text-sm">Search a rider above and tap the ★ to follow them. Their rodeos then plot on the map.</p></div>
          ) : following.map((n) => {
            const prof = profiles[n];
            const act = activeRider === n;
            return (
              <div key={n} className={`${M.card} p-3 ${act ? "ring-2 ring-[#C8000F]" : ""}`} data-testid={`m-map-rider-card-${n}`}>
                <div className="flex items-center gap-2">
                  <button onClick={() => setActiveRider(act ? null : n)} className="flex-1 min-w-0 text-left">
                    <div className="font-display font-bold text-[15px] text-white truncate">{n}</div>
                    <div className="text-[12px] text-slate-400">
                      {prof ? `${prof.counts?.rodeos ?? 0} rodeos · ${prof.counts?.upcoming ?? 0} upcoming · ` : "loading… · "}
                      {prof ? <span className="text-[#5CC46A] font-semibold">{money((prof.total_won || 0) + (prof.cfr?.total_won || 0))} won</span> : ""}
                    </div>
                  </button>
                  <button onClick={() => nav(`/riders`)} title="Full profile" data-testid={`m-map-rider-open-${n}`} className="shrink-0 p-1.5 rounded-lg bg-white/5 text-slate-300"><Users className="w-4 h-4" /></button>
                  <button onClick={() => unfollow(n)} title="Unfollow" data-testid={`m-map-rider-unfollow-${n}`} className="shrink-0 p-1.5 rounded-lg text-[#C8000F]"><Star className="w-4 h-4 fill-current" /></button>
                </div>
                <div className="text-[11px] text-slate-500 mt-1.5">{act ? "Showing this rider's pins — tap again to show everyone" : "Tap to show only this rider on the map"}</div>
              </div>
            );
          })}
        </div>
      ) : (<>
      <div className="px-3 space-y-2.5">
        {list.map((r, i) => {
          const color = pinColorFor(r); const on = sel.includes(r.id);
          return (
            <div key={r.id} className={`${M.card} p-3 transition-shadow ${focusId === r.id ? "ring-2 ring-[#3B82F6] bg-[#12233B]" : ""}`} data-testid={`m-map-list-${r.id}`}>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5" style={{ background: on ? COLORS.sel : COLORS[color] }}>{on ? sel.indexOf(r.id) + 1 : dateOrder[r.id]}</span>
                <button onClick={() => { setFocusId(r.id); selectRodeo(r); if (window.matchMedia("(orientation: portrait)").matches) sheet.setCollapsed(true); }} data-testid={`m-map-flyto-${r.id}`} className="flex-1 min-w-0 text-left">
                  <div className="font-display font-bold text-[15px] text-white">{r.name}</div>
                  <div className="text-[12px] text-slate-400">{r.city}, {r.state}</div>
                  <div className="text-[12px] text-slate-300 mt-0.5"><span className="text-[#5CC46A] font-semibold">{moneyLabel(r, displayEvents)}</span> <span className="text-slate-600">•</span> {r.km.toLocaleString()} km away</div>
                  <EntryChips r={r} dark className="mt-1.5" />
                </button>
                <button onClick={() => toggleSel(r.id)} data-testid={`m-map-compare-${r.id}`}
                  className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${on ? "bg-[#3B82F6] text-white" : "bg-white/5 text-slate-400"}`}>
                  {on ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                </button>
              </div>
              <div className="mt-2.5 pt-2.5 border-t border-white/10">
                <MRodeoActions r={r} setDlg={setDlg} />
              </div>
            </div>
          );
        })}
      </div>
      </>)}
      </>)}
      </div>
      <button onClick={() => sheet.setCollapsed(!sheet.collapsed)} data-testid="m-map-landscape-toggle"
        className={`hidden landscape:flex absolute z-[1101] top-1/2 -translate-y-1/2 items-center justify-center w-8 h-16 rounded-r-xl bg-[#161B22] border border-l-0 border-white/15 text-slate-200 shadow-md transition-all duration-300 ${sheet.collapsed ? "left-0" : "left-[340px]"}`}
        title={sheet.collapsed ? "Show list" : "Hide list"}>
        {sheet.collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
      </button>
      </div>
      <ContestantCard open={!!card} rodeoId={card?.rodeoId} name={card?.name} onClose={() => setCard(null)} />
      <MRodeoDialogs dlg={dlg} onClose={() => setDlg(null)} />
    </div>
  );
}
